import { Link, router } from "@inertiajs/react"
import { ArrowDown, ArrowUp, Pencil } from "lucide-react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { MasterclassModuleCover } from "@/components/masterclasses/masterclass-module-cover"
import { MasterclassModuleDeleteDialog } from "@/components/masterclasses/masterclass-module-delete-dialog"
import { MasterclassModuleTypeBadge } from "@/components/masterclasses/masterclass-module-type-badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  editMasterclassModulePath,
  moveDownMasterclassModulePath,
  moveUpMasterclassModulePath,
} from "@/routes"
import type { MasterclassModuleRow as MasterclassModuleRowType } from "@/types/masterclasses"

/**
 * OKS-833 — Rich module card (brand-side), replacing the OKS-55
 * `MasterclassModuleRow` table row (UX Spec §2.1). Matches the
 * masterclass-card visual language: cover band → body → action row.
 *
 * **Container, not a link** (PRD OQ1 / TRD KD5): unlike `MasterclassCard`
 * (a whole-card `<Link>`), a module card carries inline reorder/edit/delete
 * actions and has no brand-side "show" destination, so the card body is a
 * non-interactive container — only the actions are interactive. This
 * preserves the OKS-620 action a11y and avoids nested-interactive.
 *
 * **OKS-620 behavior carried verbatim** from the retired row: 44px hit
 * areas; keyboard-operable reorder; first-row ↑ / last-row ↓ `disabled` +
 * `aria-disabled`; the action cluster never shrinks off-screen; a long
 * title WRAPS (2-line clamp) rather than truncating on a single row.
 * Reorder posts `only: [...]` + Sonner toast on error — identical wire to
 * the row.
 *
 * Position display is 1-indexed (DB stores 0-indexed; render `position + 1`).
 *
 * `data-testid` family — `masterclass-module-card-{id}` / `-cover` /
 * `-position` / `-title` / `-type` / `-description` / `-move-up` /
 * `-move-down` / `-edit` for LQE Playwright + Vitest selectors (mirrors the
 * OKS-620 row testids so existing reorder-a11y specs port cleanly).
 */
export interface MasterclassModuleCardProps {
  masterclassId: number
  module: MasterclassModuleRowType & { id: number }
  /** Position among siblings — used for the disabled-state arrow guards. */
  index: number
  /** Total siblings in the list — last-card ↓ guard. */
  total: number
}

export function MasterclassModuleCard({
  masterclassId,
  module: mod,
  index,
  total,
}: MasterclassModuleCardProps) {
  const { t } = useTranslation()

  const isFirst = index === 0
  const isLast = index === total - 1
  const title = mod.title ?? ""

  const handleMove = (direction: "up" | "down") => {
    const path =
      direction === "up"
        ? moveUpMasterclassModulePath(masterclassId, mod.id)
        : moveDownMasterclassModulePath(masterclassId, mod.id)

    router.post(
      path,
      {},
      {
        preserveScroll: true,
        only: ["modules", "module_count", "can_create_module"],
        onError: () => {
          toast.error(t("masterclasses.modules.flash.reorder_failed"))
        },
      },
    )
  }

  return (
    <Card
      className="flex flex-col gap-0 overflow-hidden p-0 transition-shadow hover:shadow-md"
      data-testid={`masterclass-module-card-${mod.id}`}
      aria-label={t("masterclasses.show.modules.row.position_aria", {
        n: mod.position + 1,
        total,
        title,
      })}
    >
      {/* Cover band — 3:2; swatch fallback (module id → deterministic tone
          + GraduationCap) when no cover. Decorative alt=title on the img. */}
      <MasterclassModuleCover
        id={mod.id}
        coverImageUrl={mod.cover_image_url ?? null}
        title={title}
        size="card"
        data-testid={`masterclass-module-card-${mod.id}-cover`}
      />

      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-start gap-2">
          <span
            className="text-muted-foreground w-6 shrink-0 pt-0.5 text-center text-sm font-medium tabular-nums"
            data-testid={`masterclass-module-card-${mod.id}-position`}
          >
            {mod.position + 1}
          </span>
          {/* Full title — WRAPS to 2 lines (no single-row truncate); the
              action row below never shifts off-screen (OKS-620 guard). */}
          <h3
            className="line-clamp-2 min-w-0 flex-1 font-semibold tracking-tight"
            data-testid={`masterclass-module-card-${mod.id}-title`}
          >
            {mod.title}
          </h3>
        </div>

        <MasterclassModuleTypeBadge
          mode={mod.mode}
          data-testid={`masterclass-module-card-${mod.id}-type`}
        />

        {mod.description ? (
          <p
            className="text-muted-foreground line-clamp-3 text-sm"
            data-testid={`masterclass-module-card-${mod.id}-description`}
          >
            {mod.description}
          </p>
        ) : (
          <span
            className="text-muted-foreground text-sm"
            data-testid={`masterclass-module-card-${mod.id}-description-empty`}
          >
            {t("masterclasses.show.modules.row.description_empty")}
          </span>
        )}

        {/* Action row — ↑ ↓ ✎ 🗑, right-aligned, 44px targets,
            keyboard-operable. First ↑ / last ↓ disabled + aria-disabled
            (OKS-620 verbatim). */}
        <div className="mt-1 flex items-center justify-end gap-1 border-t pt-3">
          <Button
            variant="ghost"
            size="icon"
            type="button"
            disabled={isFirst}
            aria-disabled={isFirst}
            aria-label={t("masterclasses.show.modules.row.move_up_aria", {
              title,
            })}
            onClick={() => handleMove("up")}
            data-testid={`masterclass-module-card-${mod.id}-move-up`}
            className="size-11"
          >
            <ArrowUp className="size-4" aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            type="button"
            disabled={isLast}
            aria-disabled={isLast}
            aria-label={t("masterclasses.show.modules.row.move_down_aria", {
              title,
            })}
            onClick={() => handleMove("down")}
            data-testid={`masterclass-module-card-${mod.id}-move-down`}
            className="size-11"
          >
            <ArrowDown className="size-4" aria-hidden="true" />
          </Button>
          <Button
            asChild
            variant="ghost"
            size="icon"
            aria-label={t("masterclasses.show.modules.row.edit_aria", {
              title,
            })}
            data-testid={`masterclass-module-card-${mod.id}-edit`}
            className="size-11"
          >
            <Link href={editMasterclassModulePath(masterclassId, mod.id)}>
              <Pencil className="size-4" aria-hidden="true" />
            </Link>
          </Button>
          <MasterclassModuleDeleteDialog
            masterclassId={masterclassId}
            moduleId={mod.id}
            moduleTitle={title}
            mode={mod.mode}
            iconOnly
          />
        </div>
      </CardContent>
    </Card>
  )
}
